// Dependencies
import { IBoltTask } from "@bolt/core";
import {
  SecondaryGrid,
  useGrid,
  utils,
  mergeGridColumnsWithDefaults,
} from "@ucl/library";
import { useEffect } from "react";
import { defaultBoltTaskCompactColumns } from "./config/TaskGridConfig";
import { BoltTaskGridProps } from "./TaskGrid";
import { GridComponent } from "@syncfusion/ej2-react-grids";
import useTaskGrid from "./useTaskGrid";

interface BoltTaskCompactGridProps extends BoltTaskGridProps {
  className?: string;
  // Add the boltTaskColumnsConfiguration prop for custom column merging
  boltTaskColumnsConfiguration?: Record<string, any>;
  onlyIncludeProvidedColumnsIfSpecified?: boolean;
  onTaskComplete?: (taskId: string) => Promise<void>;
}

export const BoltTaskCompactGrid: React.FC<BoltTaskCompactGridProps> = (
  props
) => {
  const { getPredicate, createOnRecordClick } = useTaskGrid();

  useEffect(() => {
    if (props.gridLastRefreshed && gridRef.current) {
      gridRef.current?.refresh();
    }
  }, [props.gridLastRefreshed]);

  const handleTaskComplete = async (taskId: string) => {
    if (props.onTaskComplete) {
      await props.onTaskComplete(taskId);
    }

    gridRef.current?.refresh();
  };

  // Merge custom column configuration with defaults, similar to TaskGrid
  const mergedColumns = mergeGridColumnsWithDefaults(
    props.boltTaskColumnsConfiguration,
    defaultBoltTaskCompactColumns,
    {
      onlyIncludeProvidedColumnsIfSpecified:
        props.onlyIncludeProvidedColumnsIfSpecified ?? false,
    }
  );

  const { ready, gridRef, gridSettings } = useGrid<IBoltTask>({
    gridKey: props.gridKey || "bolt-task-compact-grid",
    columnsConfiguration: [
      ...Object.values(mergedColumns),
      ...(props.columnsConfiguration || []),
    ],
    dataSource:
      props.odataUrl ||
      `${utils.strings.trimEndForwardSlashForURL(
        props.apiBaseUrl
      )}/odata/bolttaskassigneescopedodata`,
    getAuthRequestHeader: props.getAuthRequestHeader,
    predicate: getPredicate(
      props.parentEntityType,
      props.parentEntityId,
      props.excludedTaskId
    ),
    gridConfiguration: {
      allowExcelExport: false,
      onRecordClick: createOnRecordClick({
        apiBaseUrl: props.apiBaseUrl,
        odataUrl: props.odataUrl,
        columnsConfiguration: props.columnsConfiguration,
        gridKey: props.gridKey,
        getAuthRequestHeader: props.getAuthRequestHeader,
        taskViewsSettings: undefined,
        gridLastRefreshed: props.gridLastRefreshed,
        taskExecutionModuleRegistry: props.taskExecutionModuleRegistry,
        formRegistry: props.formRegistry,
        parentEntityId: props.parentEntityId,
        parentEntityType: props.parentEntityType,
        onTasksChanged: props.onTasksChanged,
        isTaskOverviewReadOnly: props.isTaskOverviewReadOnly,
        onlyIncludeProvidedColumnsIfSpecified:
          props.onlyIncludeProvidedColumnsIfSpecified,
        showRelatedTasks: props.showRelatedTasks,
        handleTaskComplete: handleTaskComplete,
        refreshGrid: () => {
          gridRef.current?.refresh();
        },
        taskExecutionLeftPanelIsDocked: props.taskExecutionLeftPanelIsDocked,
        taskExecutionRightPanelIsDocked: props.taskExecutionRightPanelIsDocked,
        commentPermissions: props.commentPermissions,
      }),
    },
  });
  return (
    <section className={`bolt-task-compact-grid ${props.className || ""}`}>
      <div className="bolt-task-compact-grid-content">
        <SecondaryGrid
          ready={ready}
          gridRef={gridRef as React.RefObject<GridComponent>}
          gridSettings={gridSettings}
          className="bolt-task-compact-grid-secondary"
        />
      </div>
    </section>
  );
};
